import { getViewType } from 'widgets/toolbox/viewtype';
import { getJSONByUrl } from 'widgets/toolbox/ajax';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description CoreSearchBox implementation
 * @param SearchBox Base widget for extending
 * @returns SearchBox widget
 */
function CoreSearchBoxClassCreator(SearchBox: import('widgets/search/SearchBox').TSearchBox) {
    /**
     * @category widgets
     * @subcategory search
     * @class CoreSearchBox
     * @augments SearchBox
     * @classdesc Extended Search suggestion box
     */

    class CoreSearchBox extends SearchBox {
        prefs() {
            return {
                is1774Page: false,
                ...super.prefs()
            };
        }

        /**
         * @description Submit form simplified handler
         * @param _ event source element
         * @emits "search.process" event
         * @param event event instance
         */
        handleSubmit(_: TRefElementInstance, event: Event) {
            const searchInput = this.ref('field');
            const inputVal = searchInput.val();

            if (typeof inputVal === 'string') {
                searchInput.val(inputVal.trim());
            }

            if (this.goingByLink || !inputVal) {
                event.preventDefault();
            }

            if (!inputVal) {
                this.focusInput();
            } else {
                this.closeListbox();

                this.eventBus().emit('search.process', inputVal);
            }
        }

        /**
         * @description Send request to the server
         * @param query - requested search query
         * @returns Promise object represents server response for search suggestions
         */
        getSuggestions(query: string): Promise<void> {
            this.toggleSpinner(true);

            return getJSONByUrl(this.prefs().url, {
                q: query,
                is1774Page: this.prefs().is1774Page?.toString() ?? 'false',
                viewtype: getViewType()
            })
                .then(response => this.processResponse(query, <{ suggestions: { total: number }; success: boolean }>response))
                .catch(this.handleError.bind(this));
        }

        /**
         * @description Handle the suggestion click
         * @emits "search.suggestion.click" event
         * @param clickedItem - suggestion item, which was clicked
         */
        clickSuggestion(clickedItem) {
            const searchInput = this.ref('field');

            this.eventBus().emit('search.suggestion.click', {
                searchTerm: searchInput.val(),
                clickedItem: clickedItem
            });
        }

        /**
         * @description Handle the View All Results button click
         * @emits "search.process" event
         */
        clickViewAll() {
            const searchInput = this.ref('field');

            this.eventBus().emit('search.process', searchInput.val());
        }

        /**
         * @description Toggle clear button
         * @param isInputHasValue - is search input has value
         */
        toggleClearButton(isInputHasValue: boolean) {
            this.ref('clearButton')
                .toggleClass(this.prefs().classesClearEnabled, isInputHasValue)
                .attr('tabindex', isInputHasValue ? '0' : '-1')
                .attr('aria-hidden', String(isInputHasValue));
        }

        /**
         * @description Calculates last focusable element
         */
        calculateLastFocusableElement() {
            const focusableElements: Array<HTMLElement> | unknown = this.callIfExists('getFocusableElements', ['listbox']);

            if (focusableElements && Array.isArray(focusableElements) && focusableElements.length) {
                this.ref('self').attr('data-ref-last-focus-element', focusableElements[focusableElements.length - 1].dataset.ref);
            }
        }

        /**
         * @description Open a Listbox (Combobox dropdown)
         */
        openListbox() {
            super.openListbox();
            this.calculateLastFocusableElement();
        }
    }

    return CoreSearchBox;
}

export type TCoreSearchBox = ReturnType<typeof CoreSearchBoxClassCreator>;

export type TCoreSearchBoxInstance = InstanceType<TCoreSearchBox>;

export default CoreSearchBoxClassCreator;
